<script setup lang="ts">
import { ref } from 'vue';
import type { MapProvider } from '@gpx-spa/geo-domain';
import { GpxMap } from '@gpx-spa/ui';
import ProgressSpinner from 'primevue/progressspinner';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_OVERLAY,
} from '@/constants/progress-spinner';

withDefaults(
  defineProps<{
    mapProvider: MapProvider;
    showMapBusyOverlay: boolean;
    /** Omit outer card and "Map" heading (parent provides Route preview). */
    embedded?: boolean;
  }>(),
  { embedded: false },
);

const emit = defineEmits<{
  ready: [];
}>();

const mapSurfaceRef = ref<HTMLElement | null>(null);

function onMapSurfaceReady() {
  const leaflet = mapSurfaceRef.value?.querySelector('.leaflet-container');
  if (leaflet instanceof HTMLElement) {
    leaflet.tabIndex = -1;
  }
  emit('ready');
}

defineExpose({
  mapSurfaceRef,
});
</script>

<template>
  <section
    v-if="!embedded"
    class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
  >
    <h2 class="text-sm font-semibold text-gpx_black-200">Map</h2>
    <div
      class="relative mt-3 min-h-[420px]"
      :aria-busy="showMapBusyOverlay ? 'true' : 'false'"
    >
      <div
        v-if="showMapBusyOverlay"
        class="pointer-events-auto absolute inset-0 z-[500] flex cursor-wait items-center justify-center rounded-md bg-white/80 backdrop-blur-[1px]"
        role="status"
        aria-label="Loading"
      >
        <ProgressSpinner
          :stroke-width="PVT_SPINNER_STROKE_WIDTH"
          :style="PVT_SPINNER_STYLE_OVERLAY"
          aria-hidden="true"
        />
      </div>
      <div class="h-[420px] w-full" tabindex="-1" ref="mapSurfaceRef">
        <GpxMap
          class="block size-full"
          :map-provider="mapProvider"
          @ready="onMapSurfaceReady"
        />
      </div>
    </div>
  </section>
  <div
    v-else
    class="relative min-h-[420px]"
    :aria-busy="showMapBusyOverlay ? 'true' : 'false'"
  >
    <div
      v-if="showMapBusyOverlay"
      class="pointer-events-auto absolute inset-0 z-[500] flex cursor-wait items-center justify-center rounded-md bg-white/80 backdrop-blur-[1px]"
      role="status"
      aria-label="Loading"
    >
      <ProgressSpinner
        :stroke-width="PVT_SPINNER_STROKE_WIDTH"
        :style="PVT_SPINNER_STYLE_OVERLAY"
        aria-hidden="true"
      />
    </div>
    <div class="h-[420px] w-full" tabindex="-1" ref="mapSurfaceRef">
      <GpxMap
        class="block size-full"
        :map-provider="mapProvider"
        @ready="onMapSurfaceReady"
      />
    </div>
  </div>
</template>
